import React from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowUpRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/I18nContext";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const { t } = useLang();

  const catLabel = t(`cat.${product.category}`, product.category);
  const compactSpecs = [
    product.specs?.Leistung || product.specs?.Heizleistung,
    product.specs?.Kältemittel,
  ].filter(Boolean);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
    toast.success(`${product.name} ${t("card.toastAdded")}`);
  };

  return (
    <Link
      to={`/shop/${product.slug}`}
      className="product-card group"
      data-testid={`product-card-${product.slug}`}
    >
      <div className="img-wrap">
        <img src={product.image} alt={product.name} loading="lazy" />
        {product.badge && (
          <div className="absolute top-4 left-4 z-10 inline-flex items-center px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase font-semibold bg-[#0F2A48] text-white border border-white/20 shadow-lg shadow-black/20" data-testid={`badge-${product.slug}`}>
            {product.badge}
          </div>
        )}
        <div className="absolute top-4 right-4 z-10 w-9 h-9 border border-[#0F2A48]/40 bg-white/85 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-y-1 group-hover:translate-y-0">
          <ArrowUpRight size={14} className="text-[#0F2A48]"/>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="eyebrow">{catLabel}</div>
          {product.manufacturer && <span className="text-[10px] font-bold tracking-[0.12em] uppercase text-[#8BC7FF]" data-testid={`manufacturer-${product.slug}`}>{product.manufacturer}</span>}
        </div>
        <h3 className="font-display text-lg leading-tight tracking-tight mb-3 group-hover:opacity-80 transition-opacity">
          {product.name}
        </h3>
        <p className="text-sm text-white/55 line-clamp-2 mb-4 leading-relaxed">
          {product.short_description}
        </p>
        {compactSpecs.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid={`compact-specs-${product.slug}`}>
            {compactSpecs.map((value) => <span key={value} className="px-2.5 py-1 border border-white/10 bg-[#0A1F38]/60 text-[10px] text-white/70">{value}</span>)}
          </div>
        )}
        <div className="flex items-end justify-between gap-3 pt-5 border-t border-white/10">
          <div className="flex flex-col min-w-0 flex-1">
            {(() => {
              const pn = product.price_note || "";
              const hasPrice = typeof product.price_from === "number" && product.price_from > 0;
              // Parse "ab X.XXX € Netto..." or "Preis auf Anfrage · ab € X.XXX · Netto"
              const priceMatch = pn.match(/€\s*[\d.,]+/) || pn.match(/[\d.,]+\s*€/);
              const isOnRequest = /anfrage/i.test(pn) && !hasPrice;
              if (isOnRequest) {
                return (
                  <>
                    <span className="text-[10px] tracking-[0.15em] uppercase text-amber-300/80 font-medium">{t("card.onRequest")}</span>
                    {priceMatch && <span className="text-[11px] tracking-[0.12em] uppercase text-white/55 mt-1">ab {priceMatch[0]}</span>}
                  </>
                );
              }
              if (hasPrice) {
                return (
                  <>
                    <span className="font-display text-lg leading-none tracking-tight">€ {product.price_from.toLocaleString("de-AT", { maximumFractionDigits: 0 })}</span>
                    <span className="text-[9px] tracking-[0.15em] uppercase text-white/40 mt-1">{t("card.netto")}</span>
                  </>
                );
              }
              return <span className="text-[11px] tracking-[0.12em] uppercase text-white/45 line-clamp-2">{pn}</span>;
            })()}
          </div>
          <button
            onClick={handleAdd}
            data-testid={`add-to-cart-${product.slug}`}
            className="flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase font-semibold text-white hover:opacity-80 transition-opacity whitespace-nowrap shrink-0 pb-0.5"
          >
            <Plus size={13} strokeWidth={2}/> {t("card.inquire")}
          </button>
        </div>
      </div>
    </Link>
  );
}
